<script setup lang="ts">
	import { inject, ref, type Component } from "vue";

	const confirmComponent = inject<Component>("confirmComponent");
	const dialog = ref<HTMLDialogElement>();

	const title = ref("");
	const text = ref("");
	const cancelText = ref("Cancel");
	const okText = ref("Ok");
	let waitOnConfirm = false;

	const loading = ref(false);
	let resolve: ((success: boolean | (() => void)) => void) | undefined =
		undefined;

	type ConfirmOptions = {
		title: string;
		text: string;
		cancelText?: string;
		okText?: string;
		waitOnConfirm?: boolean;
	};

	function open<T extends ConfirmOptions>(
		options: T,
	): Promise<
		T extends ConfirmOptions & { waitOnConfirm: true }
			? () => void
			: boolean
	> {
		title.value = options.title;
		text.value = options.text;
		cancelText.value = options.cancelText ?? "Cancel";
		okText.value = options.okText ?? "Ok";
		waitOnConfirm =
			"waitOnConfirm" in options ? !!options.waitOnConfirm : false;
		dialog.value?.showModal();

		return new Promise(r => {
			resolve = r as
				| ((success: boolean | (() => void)) => void)
				| undefined;
		});
	}

	const cancel = () => {
		dialog.value?.close();
		resolve?.(false);
	};

	const confirm = async () => {
		if (waitOnConfirm) {
			loading.value = true;
			resolve?.(() => {
				loading.value = false;
				dialog.value?.close();
			});
		} else {
			resolve?.(true);
			dialog.value?.close();
		}
	};

	defineExpose({ open });
</script>
<template>
	<dialog
		ref="dialog"
		@close="resolve?.(false)"
	>
		<component
			:is="confirmComponent"
			v-if="confirmComponent"
			:title="title"
			:text="text"
			:cancel-text="cancelText"
			:ok-text="okText"
			:loading="loading"
			@cancel="cancel"
			@confirm="confirm"
		/>
	</dialog>
</template>

<style scoped>
	dialog {
		margin: auto;
		border: none;
		background-color: transparent;
		overflow: visible;
	}
</style>
